import React, { useState, useEffect, useCallback, useRef } from 'react';
import { dashboardAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { bdaOptions, resolveBdeIdentity } from '../utils/bdeOwnership';
import { Calendar, Video, Trash2, ExternalLink, Plus, RefreshCw, CalendarX, Link2, CalendarClock, X } from 'lucide-react';
import MeetingScheduler from './MeetingScheduler';
import MeetingLinkForm from './MeetingLinkForm';

/**
 * Meeting Schedule.
 *
 * This is the team's "Meetings Schedule" sheet, in the app: one row per
 * meeting, in the sheet's own columns — Date, Day, Company, POC, Country, time
 * in IST, the BDA, Shree's availability, a Done flag and a free-text note.
 *
 * Two ways a meeting gets here:
 *   - WE book it, through the scheduler, which creates the Outlook event and a
 *     Teams link (the same form the pre-sales page uses); or
 *   - the CLIENT books it and sends a link, which is recorded as-is with no
 *     invitation of ours (MeetingLinkForm).
 *
 * **Everyone sees every meeting.** The schedule is a shared team view — the
 * whole point is that the sales team can see what is booked no matter who
 * booked it — so unlike the rest of the app this page does not scope rows to
 * the signed-in BDA. The BDA dropdown is there to narrow it by choice.
 */

const RANGES = [
  { id: 'all', label: 'All' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'past', label: 'Past' }
];

const IST = 'Asia/Kolkata';
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

const fmt = (iso, opts) => {
  if (!iso) return '';
  try { return new Intl.DateTimeFormat('en-GB', { timeZone: IST, ...opts }).format(new Date(iso)); }
  catch { return ''; }
};
// The sheet is kept in IST, so the schedule reads in IST whatever zone it was booked in.
const dateIST = (iso) => fmt(iso, { day: '2-digit', month: 'short', year: '2-digit' });
const dayIST = (iso) => fmt(iso, { weekday: 'short' });
/** The value an <input type="datetime-local"> wants, in the viewer's own zone. */
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const timeIST = (iso) => fmt(iso, { hour: 'numeric', minute: '2-digit', hour12: true });

/** A cell you can type straight into, like the sheet. Saves when you leave it. */
const EditableCell = ({ value, onSave, placeholder, width = 'w-full' }) => {
  const [text, setText] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const original = useRef(value || '');
  useEffect(() => { setText(value || ''); original.current = value || ''; }, [value]);

  const commit = async () => {
    if (text === original.current) return;
    setSaving(true);
    try { await onSave(text); original.current = text; }
    catch { setText(original.current); }
    finally { setSaving(false); }
  };

  return (
    <input
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
        if (e.key === 'Escape') { setText(original.current); e.target.blur(); }
      }}
      placeholder={placeholder}
      title={text || placeholder}
      className={`${width} min-w-0 bg-transparent border border-transparent hover:border-gray-300 focus:border-[#679632] focus:bg-white rounded px-1 py-1 text-[13px] outline-none ${saving ? 'opacity-50' : ''}`}
    />
  );
};

/** Move a meeting to another time. Outlook tells the attendees, not us. */
const RescheduleDialog = ({ meeting, onClose, onSave }) => {
  const [when, setWhen] = useState(toLocalInput(meeting.scheduled_at));
  const [minutes, setMinutes] = useState(meeting.duration_minutes || 30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const clientBooked = meeting.link_source === 'client';

  const submit = async () => {
    setError('');
    if (!when) return setError('Pick the new date and time.');
    setBusy(true);
    try { await onSave(meeting, when, minutes); }
    catch (e) { setError(e.message || 'Could not move it.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <CalendarClock size={18} className="text-[#0f6cbd]" />
          <h3 className="text-sm font-semibold text-gray-900">Move this meeting</h3>
          <button type="button" onClick={onClose} className="ml-auto p-1 text-gray-400 hover:text-gray-700"><X size={16} /></button>
        </div>

        <p className="text-sm text-gray-600">
          {meeting.company_name || 'This meeting'} — currently {dateIST(meeting.scheduled_at)}, {timeIST(meeting.scheduled_at)} IST
        </p>

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">New date and time (your local time)</label>
            <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Minutes</label>
            <input type="number" min="5" step="5" value={minutes} onChange={(e) => setMinutes(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
          </div>
        </div>

        <p className="text-xs text-gray-500">
          {clientBooked
            ? 'The client booked this one, so this only moves it on our schedule — nobody is notified from here.'
            : 'Outlook will send the attendees the updated invitation.'}
        </p>

        {error && <p className="text-sm text-red-700 bg-red-50 rounded px-3 py-2">{error}</p>}

        <div className="flex items-center gap-2">
          <button type="button" onClick={submit} disabled={busy}
            className="px-4 py-2 bg-[#0f6cbd] text-white rounded-lg text-sm font-medium hover:bg-[#0c5a9e] disabled:opacity-50">
            {busy ? 'Moving…' : 'Move it'}
          </button>
          <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  );
};

const Meetings = ({ leads = [], onNavigateToPreSales }) => {
  const { user } = useAuth();
  const [roster, setRoster] = useState(null);
  const identity = resolveBdeIdentity(user, roster);

  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState('all');       // the sheet shows everything by default
  const [bdeFilter, setBdeFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showForm, setShowForm] = useState(false);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [rescheduling, setRescheduling] = useState(null);   // the meeting being moved

  useEffect(() => {
    dashboardAPI.getBDEUsers()
      .then(res => setRoster(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setRoster([]));
  }, []);

  const fetchMeetings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await dashboardAPI.getMeetings();
      if (res.success) setMeetings(res.data || []);
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMeetings(); }, [fetchMeetings]);

  const flash = (type, text, ms = 5000) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), ms);
  };

  /** Save one cell without refetching the whole list — the row updates in place. */
  const patch = async (m, changes) => {
    const res = await dashboardAPI.updateMeeting(m.id, changes);
    if (!res.success) { flash('error', res.error || 'Could not save that.'); throw new Error(res.error); }
    setMeetings(list => list.map(x => (x.id === m.id ? { ...x, ...changes } : x)));
  };

  const visible = meetings.filter(m => {
    // Deliberately NOT scoped to the signed-in BDA — see the note at the top.
    if (bdeFilter !== 'All' && (m.bde_name || '') !== bdeFilter) return false;
    if (search) {
      const hay = `${m.company_name || ''} ${m.meeting_title || ''} ${m.poc_name || ''} ${m.country || ''} ${m.bde_name || ''} ${m.email_note || ''}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    const when = m.scheduled_at ? new Date(m.scheduled_at) : null;
    if (!when) return range === 'all';
    const now = new Date();
    if (range === 'upcoming') return when >= now;
    if (range === 'past') return when < now;
    if (range === 'today') return startOfDay(when).getTime() === startOfDay(now).getTime();
    if (range === 'week') {
      const end = new Date(startOfDay(now));
      end.setDate(end.getDate() + 7);
      return when >= startOfDay(now) && when <= end;
    }
    return true;
  }).sort((a, b) => new Date(a.scheduled_at || 0) - new Date(b.scheduled_at || 0));

  const cancel = async (m) => {
    const comment = window.prompt(
      m.link_source === 'client'
        ? `Mark "${m.meeting_title || 'this meeting'}" as cancelled?\n\nThe client booked this one, so nobody is notified from here. Note (optional):`
        : `Cancel "${m.meeting_title || 'this meeting'}"?\n\nThe attendees are notified. Add a note for them (optional):`,
      ''
    );
    if (comment === null) return;
    try {
      const res = await dashboardAPI.cancelMeeting(m.id, comment);
      if (res.success) {
        flash(res.warning ? 'error' : 'success', res.warning || res.message || 'Meeting cancelled.');
        fetchMeetings();
      } else flash('error', res.error || 'Could not cancel.');
    } catch (e) { flash('error', e.response?.data?.error || e.message); }
  };

  /**
   * Move a meeting.
   *
   * The server patches the Outlook event as well when the booking has one, so
   * the attendees are told the new time by Outlook itself — which is the only
   * thing they actually act on. A meeting the client booked has no event of
   * ours, so it just moves here and the note says as much.
   */
  const reschedule = async (m, whenLocal, minutes) => {
    const res = await dashboardAPI.updateMeeting(m.id, {
      scheduled_at: new Date(whenLocal).toISOString(),
      duration_minutes: Number(minutes) || m.duration_minutes || 30,
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    if (!res.success) throw new Error(res.error || 'Could not move it.');
    setRescheduling(null);
    fetchMeetings();
    flash(res.warning ? 'error' : 'success',
      res.warning || (m.link_source === 'client'
        ? 'Moved. The client booked this one, so nobody was notified from here.'
        : 'Moved — Outlook has told the attendees.'));
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.meeting_title || 'this meeting'}" from the schedule? Use Cancel instead if the attendees still need telling.`)) return;
    try {
      const res = await dashboardAPI.deleteMeeting(m.id);
      if (res.success) { flash('success', 'Removed from the schedule.'); fetchMeetings(); }
      else flash('error', res.error || 'Could not remove it.');
    } catch (e) { flash('error', e.response?.data?.error || e.message); }
  };

  const leadOptions = (leads || []).filter(l => l && (l.company_name || '').trim());
  const th = 'px-2 py-2 text-left text-[11px] font-semibold text-gray-700 uppercase truncate';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Calendar size={20} className="text-[#679632]" /> Meeting Schedule
        </h2>
        <span className="text-sm text-gray-500">({visible.length})</span>
        <span className="text-xs text-gray-400">· everyone's meetings, times in IST</span>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company, POC, country, BDA"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-64"
          />
          <select
            value={bdeFilter}
            onChange={(e) => setBdeFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="All">All BDAs</option>
            {bdaOptions(roster).map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
          </select>
          <button onClick={fetchMeetings} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50" title="Refresh">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => { setShowLinkForm(v => !v); setShowForm(false); }}
            className="px-3 py-2 border-2 border-[#0f6cbd] text-[#0f6cbd] rounded-lg text-sm font-medium hover:bg-[#eaf2fb] flex items-center gap-1"
            title="The client booked it and sent a link"
          >
            <Link2 size={16} /> Add client's link
          </button>
          <button
            onClick={() => { setShowForm(v => !v); setShowLinkForm(false); }}
            className="px-3 py-2 bg-[#679632] text-white rounded-lg text-sm font-medium hover:bg-[#587f2b] flex items-center gap-1"
            title="Book it in Outlook and generate a Teams link"
          >
            <Plus size={16} /> Schedule meeting
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {RANGES.map(r => (
          <button
            key={r.id}
            onClick={() => setRange(r.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${range === r.id ? 'bg-[#e1f0da] text-[#496821] border-2 border-[#679632]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {message.text && (
        <div className={`p-3 rounded-lg text-sm ${message.type === 'error' ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>
          {message.text}
        </div>
      )}

      {visible.length > 0 && meetings.length > visible.length && (
        <p className="text-xs text-gray-500">
          {meetings.length - visible.length} more hidden by the current filter —
          {' '}
          <button type="button" onClick={() => { setRange('all'); setBdeFilter('All'); setSearch(''); }}
            className="text-[#679632] underline">show everything</button>
        </p>
      )}

      {showLinkForm && (
        <MeetingLinkForm
          leadOptions={leadOptions}
          roster={roster || []}
          onSaved={() => { setShowLinkForm(false); fetchMeetings(); flash('success', 'Added to the schedule.'); }}
          onCancel={() => setShowLinkForm(false)}
        />
      )}

      {showForm && (
        <MeetingScheduler
          leadOptions={leadOptions}
          onScheduled={(created) => {
            fetchMeetings();
            if (created?.scheduled_at && new Date(created.scheduled_at) < new Date() && range !== 'all') {
              setRange('all');
              flash('success', 'That time has already passed, so the list has switched to All.');
            }
          }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {visible.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-400 text-sm">No meetings {range === 'all' ? 'on the schedule yet' : `in "${range}"`}</p>
          {meetings.length > 0 && (
            <p className="text-xs text-gray-500 mt-2">
              {meetings.length} on record —
              {' '}
              <button type="button" onClick={() => { setRange('all'); setBdeFilter('All'); setSearch(''); }}
                className="text-[#679632] underline">show everything</button>
            </p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div>
            {/* table-fixed + these widths = the whole schedule in one view, no
                sideways scrolling. The columns share the space they are given
                rather than each demanding its content's width. */}
            <table className="w-full table-fixed">
              <colgroup>
                <col className="w-[7%]" /><col className="w-[5%]" /><col className="w-[13%]" />
                <col className="w-[10%]" /><col className="w-[7%]" /><col className="w-[7%]" />
                <col className="w-[11%]" /><col className="w-[9%]" /><col className="w-[8%]" />
                <col className="w-[10%]" /><col className="w-[6%]" /><col className="w-[7%]" />
              </colgroup>
              <thead className="bg-[#f3f7ee] border-b-2 border-[#d7e5c8]">
                <tr>
                  <th className={th}>Date</th>
                  <th className={th}>Day</th>
                  <th className={th}>Company</th>
                  <th className={th}>POC Name</th>
                  <th className={th}>Country</th>
                  <th className={th}>Time IST</th>
                  <th className={th}>BDA</th>
                  <th className={th}>Shree (IST)</th>
                  <th className={th}>Status</th>
                  <th className={th}>Email</th>
                  <th className={th}>Link</th>
                  <th className={`${th} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visible.map(m => {
                  const past = m.scheduled_at && new Date(m.scheduled_at) < new Date();
                  const cancelled = (m.status || '') === 'Cancelled';
                  const clientBooked = m.link_source === 'client';
                  return (
                    <tr key={m.id} className={`hover:bg-gray-50 ${cancelled ? 'opacity-60' : past ? 'opacity-80' : ''}`}>
                      <td className="px-2 py-2 text-[13px] text-gray-900 font-medium truncate">{dateIST(m.scheduled_at) || '-'}</td>
                      <td className="px-2 py-2 text-[13px] text-gray-600 truncate">{dayIST(m.scheduled_at) || '-'}</td>
                      <td className="px-2 py-2 text-[13px] text-gray-900">
                        <span className={cancelled ? 'line-through' : ''} title={m.company_name || ''}>{m.company_name || '-'}</span>
                        {cancelled && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-100">cancelled</span>}
                        {clientBooked && !cancelled && (
                          <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-[#0f6cbd] border border-blue-100" title="The client booked this one">client</span>
                        )}
                      </td>
                      <td className="px-2 py-2"><EditableCell value={m.poc_name} placeholder="—" onSave={(v) => patch(m, { poc_name: v })} /></td>
                      <td className="px-2 py-2"><EditableCell value={m.country} placeholder="—" onSave={(v) => patch(m, { country: v })} /></td>
                      <td className="px-2 py-2 text-[13px] text-gray-900">
                        {timeIST(m.scheduled_at) || '-'}
                        <span className="block text-[11px] text-gray-400">{m.duration_minutes || 30} min</span>
                      </td>
                      <td className="px-2 py-2 text-[13px] text-gray-700 truncate" title={m.bde_name || m.organizer_name || ''}>{m.bde_name || m.organizer_name || '-'}</td>
                      <td className="px-2 py-2"><EditableCell value={m.shree_time} placeholder="—" onSave={(v) => patch(m, { shree_time: v })} /></td>
                      <td className="px-2 py-2">
                        <select
                          value={m.progress_status || ''}
                          onChange={(e) => patch(m, { progress_status: e.target.value }).catch(() => {})}
                          className={`w-full text-[13px] rounded px-1 py-1 border ${m.progress_status === 'Done' ? 'bg-green-50 text-green-800 border-green-200' : 'bg-transparent border-transparent hover:border-gray-300'}`}
                        >
                          <option value="">—</option>
                          <option value="Done">Done</option>
                          <option value="Pending">Pending</option>
                          <option value="No show">No show</option>
                        </select>
                      </td>
                      <td className="px-2 py-2"><EditableCell value={m.email_note} placeholder="—" onSave={(v) => patch(m, { email_note: v })} /></td>
                      <td className="px-2 py-2">
                        {m.join_url ? (
                          <a href={m.join_url} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 border border-[#0f6cbd] text-[#0f6cbd] rounded text-[11px] font-medium hover:bg-[#eaf2fb]"
                            title={m.join_url}>
                            <Video size={12} /> Join
                          </a>
                        ) : (
                          <EditableCell value="" placeholder="paste link" onSave={(v) => patch(m, { join_url: v })} />
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center justify-end gap-0.5">
                          {!cancelled && (
                            <button onClick={() => setRescheduling(m)}
                              className="p-1 text-gray-500 hover:text-[#0f6cbd] hover:bg-blue-50 rounded"
                              title="Move this meeting to another time">
                              <CalendarClock size={15} />
                            </button>
                          )}
                          {!cancelled && !past && (
                            <button onClick={() => cancel(m)}
                              className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                              title={clientBooked ? 'Cancel (the client booked it, so nobody is notified from here)' : 'Cancel and notify the attendees'}>
                              <CalendarX size={15} />
                            </button>
                          )}
                          {onNavigateToPreSales && m.lead_id && (
                            <button onClick={() => onNavigateToPreSales(m.lead_id)}
                              className="p-1 text-gray-500 hover:text-[#679632] hover:bg-gray-100 rounded" title="Open the pre-sales form">
                              <ExternalLink size={14} />
                            </button>
                          )}
                          <button onClick={() => remove(m)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Remove this record">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rescheduling && (
        <RescheduleDialog
          meeting={rescheduling}
          onClose={() => setRescheduling(null)}
          onSave={reschedule}
        />
      )}

      <p className="text-xs text-gray-400">
        Click any of POC, Country, Shree, Status, Email or a missing link to edit it — it saves when you click away.
        {identity.restrictToOwn ? ' Everyone can see and edit the whole schedule.' : ''}
      </p>
    </div>
  );
};

export default Meetings;
